<template>
  <v-card flat>
    <v-card-text>
      <!-- Summary Cards -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="3">
          <v-card color="green darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">{{ report.total_count }}</div>
            <div class="caption">Items with Sales</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="blue darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.grand_total_qty_sold }}
            </div>
            <div class="caption">Total Units Sold</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="purple darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.grand_total_revenue) }}
            </div>
            <div class="caption">Total Revenue</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="teal darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h6 font-weight-bold">
              {{ report.period_days }} days
            </div>
            <div class="caption">
              {{ report.from_date }} - {{ report.to_date }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table
        v-if="report"
        :items="report.items"
        :headers="headers"
        :search="search"
        item-key="id"
        dense
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search items..."
            dense
            class="mb-2"
          />
        </template>
        <template v-slot:item.total_qty_sold="{ item }">
          <span class="font-weight-bold green--text text--darken-2">
            {{ item.total_qty_sold }}
          </span>
        </template>
        <template v-slot:item.total_revenue="{ item }">
          {{ formatCurrency(item.total_revenue) }}
        </template>
        <template v-slot:item.avg_daily_sales="{ item }">
          {{ item.avg_daily_sales }}
        </template>
        <template v-slot:item.current_stock="{ item }">
          <v-chip
            :color="
              item.current_stock <= 0
                ? 'red'
                : item.current_stock < 5
                ? 'orange'
                : 'green'
            "
            dark
            small
          >
            {{ item.current_stock }}
          </v-chip>
        </template>
        <template v-slot:item.days_until_stockout="{ item }">
          <v-chip
            :color="getStockoutColor(item.days_until_stockout)"
            dark
            small
          >
            {{
              item.days_until_stockout >= 999
                ? "999+"
                : item.days_until_stockout
            }}
          </v-chip>
        </template>
        <template v-slot:item.unit_price="{ item }">
          {{ formatCurrency(item.unit_price) }}
        </template>
        <template v-slot:item.selling_price="{ item }">
          {{ formatCurrency(item.selling_price) }}
        </template>
      </v-data-table>

      <!-- Empty State -->
      <div v-if="!report" class="text-center grey--text pa-10">
        <v-icon size="64" color="grey lighten-1">mdi-trending-up</v-icon>
        <p class="mt-4 subtitle-1">
          Click "Generate" to see which items are selling the fastest.
        </p>
        <p class="caption grey--text">
          Uses the date range from the filters above. Default: last 30 days.
        </p>
      </div>

      <!-- No Results -->
      <div
        v-if="report && report.items && report.items.length === 0"
        class="text-center pa-10"
      >
        <v-icon size="64" color="grey">mdi-cart-off</v-icon>
        <p class="mt-4 subtitle-1 grey--text">
          No sales data found for the selected period.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "FastestMovingReport",
  props: {
    filters: { type: Object, required: true },
  },
  data() {
    return {
      report: null,
      search: "",
      headers: [
        { text: "#", value: "rank", width: "50px", sortable: false },
        { text: "Item Name", value: "item_name" },
        { text: "Type", value: "item_type" },
        { text: "Supplier", value: "supplier" },
        { text: "Batch", value: "batch_name" },
        { text: "Qty Sold", value: "total_qty_sold", align: "center" },
        { text: "Revenue", value: "total_revenue", align: "end" },
        { text: "Transactions", value: "transaction_count", align: "center" },
        { text: "Avg Daily Sales", value: "avg_daily_sales", align: "center" },
        { text: "Current Stock", value: "current_stock", align: "center" },
        {
          text: "Days to Stockout",
          value: "days_until_stockout",
          align: "center",
        },
        { text: "Unit Cost", value: "unit_price", align: "end" },
        { text: "Selling Price", value: "selling_price", align: "end" },
      ],
    };
  },
  methods: {
    ...mapActions({
      fetchReport: "inventory_reports/get_fastest_moving_report",
    }),
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getStockoutColor(days) {
      if (days <= 0) return "red darken-3";
      if (days <= 7) return "red";
      if (days <= 14) return "orange";
      if (days <= 30) return "amber darken-1";
      return "green";
    },
    async generate() {
      try {
        let params = {};
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;
        if (this.filters.item_type_id)
          params.item_type_id = this.filters.item_type_id;
        if (this.filters.supplier) params.supplier = this.filters.supplier;
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        let data = await this.fetchReport(params);
        // Add rank numbers
        if (data && data.items) {
          data.items = data.items.map((item, idx) => ({
            ...item,
            rank: idx + 1,
          }));
        }
        this.report = data;
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error generating fastest moving report: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>
